<template>
  <div id="map" class="map-container">
    <TrailDataCard floating-layout/>
    <MapControls/>
    <MapButtons :button-clicked="buttonTapped" :buttons="buttons"/>
    <BaseMapStylesDialog />
    <EditRecordingDetails />
    <LoadingDialog :show-loading-dialog="loading" />
    <MapDataPOIDetailDialog/>
    <GoToLocationDialog />
    <WaypointDialog />
    <HikeMeasurementPanel v-if="isActive" context="hike" />
    <MapOptionsDialog
        @edit-details="editRecording"
        @edit="editRecordingInPlanner"
        @waypoints="openWaypoints"
        @goto="showGoTo"
        @flyover="showFlyover"
        @print="openPrintMap"
        @measurement="showMeasurement"
        @report-issue="reportIssue"
        @create="createNewHike"
        :show-edit-details="canEditRecording"
        :edit-hike="true"
        :show-create="false"
        :show-waypoints="true"
        :show-go-to="true"
        :show-flyover="true"
        :show-measurement="true"
        :show-print="true"
        :show-report-issue="false"
      />
    <div class="bottom-controls-container">
      <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="mapInstance?.getCenter()?.lat || 0" />
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { mapGetters } from 'vuex';
import BaseMapStylesDialog from '../../apps/./base_map_dialog/BaseMapStylesDialog.vue';
import MapButtons from '../../apps/components/MapButtons.vue';
import MapControls from '../../apps/components/MapControls.vue';
import EditRecordingDetails from '../../apps/edit_recording_details/EditRecordingDetails.vue';
import TrailDataCard from '../../apps/trail_data_card/TrailDataCard.vue';
import graph_client from '../../utils/graphql/graph_client';
import { GetRecordedTrailById } from '../../utils/graphql/recorded-trail-graph';
import { HiikerMapStyle, ThreeDimensionalMapManager } from '../../utils/models/MapStyles';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { buildRecordingShareConfig } from '../flyover_share/recordingShareConfig';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from 'bootstrap'
import MapOptionsDialog from '../map_options_dialog/MapOptionsDialog.vue';
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";
import ScaleBar from "../components/ScaleBar.vue";
import GoToLocationDialog from "../go_to_location_dialog/GoToLocationDialog.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import HikeMeasurementPanel from "../hike_map/components/HikeMeasurementPanel.vue";
import emitter from "../../utils/bus";
import {LayerRegistry, MapLayerCategory} from "../../utils/layer-registry";
import { ROUTE_STYLES } from "../../utils/constants/route-styles";


export default {
  name: 'RecordingMap',
  components: {
    LoadingDialog,
    EditRecordingDetails,
    BaseMapStylesDialog,
    MapOptionsDialog,
    MapButtons,
    MapControls,
    TrailDataCard,
    MapDataPOIDetailDialog,
    ScaleBar,
    GoToLocationDialog,
    WaypointDialog,
    HikeMeasurementPanel
  },
  data() {
    return {
      loading: true,
      buttons: [
        {
          id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true,
        },
        {
          id: 1, name: 'Options', icon: 'add', enabled: false, dropIcon: false,
        },
      ],
      geojson: null,
      mapInstance: null,
      recording: null,
      recordingSlug: null,
    };
  },
  computed: {
    ...mapGetters('hikeMeasurement', ['isActive']),
    canEditRecording() {
      const ownerFirebaseId = this.recording?.publicUser?.firebaseUserId || this.recording?.publicUser?.firebase_user_id;
      const currentUserFirebaseId = this.getCurrentUserFirebaseId();
      if (!ownerFirebaseId || !currentUserFirebaseId) return false;
      return ownerFirebaseId === currentUserFirebaseId;
    }
  },
  async mounted() {
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
    const isPro = await RevenueCatWrapper.isProOrProPlus();
    const mapStyle = HiikerMapStyle.getCurrentStyle(isPro);
    window.map = new mapboxgl.Map({
      container: 'map',
      style: mapStyle.styleUrl,
      center: window.lonlat || [0, 0],
      zoom: 12,
    });
      this.mapInstance = window.map; // Store instance locally for scale bar
      window.layerRegistry = new LayerRegistry(window.map);

      // Handle resize events to ensure the map fills the screen
      window.addEventListener('resize', this.resizeMap);
    this.resizeMap();

    // Add a map on load
    window.map.on('load', async () => {
      emitter.emit('map-loaded');
      await this.fetchData();
      this.addToMap();
    });
    window.map.on('style.load', () => {
      emitter.emit('map-style-loaded');
      this.addToMap();
      // Re-add measurement line if measurement mode is active
      this.$store.dispatch('hikeMeasurement/readdMeasurementToMap');
    });

    // Some modules re-add their layers after emitting this event. Listening
    // to it ensures our trail line is restored if other handlers removed it
    // when the style changed.
    emitter.on('map-style-loaded', this.addToMap);
  },
  methods: {
    getCurrentUserFirebaseId() {
      const user = window.user;
      return user?.firebaseUserId || user?.firebase_user_id || null;
    },
    resizeMap() {
      // Ensure map resizes when window size changes
      if (window.map) {
        window.map.resize();
      }
    },
    async buttonTapped(button) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      switch (button) {
        case 0:
          // Map Layers
          if (window.user) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1:
          // Options
          this.closeMobilePanels();
          const optionsEl = document.getElementById('map-options-dialog');
          if (optionsEl) Modal.getOrCreateInstance(optionsEl).show();
          break;
        default:
          break;
      }
    },
    async fetchData() {
      this.recordingSlug = window.recording_id;
      console.log('RecordingMap fetchData - window.recording_id:', window.recording_id);
      const response = await graph_client.request(GetRecordedTrailById, { slug: `${this.recordingSlug}` });
      console.log('RecordingMap fetchData - response:', response);
      const { recordingBySlug } = response;
      this.recording = recordingBySlug;
      console.log('RecordingMap fetchData - recording obfuscatedSlug:', this.recording.obfuscatedSlug);
      // Use the obfuscatedSlug for editing, not the window.recording_id
      this.recordingSlug = this.recording.obfuscatedSlug;
      const geojson = JSON.parse(this.recording.lineString);
      this.geojson = geojson;
      window.trailCardDataRecording(this.recording);
    },
    addToMap() {
      if (!this.geojson) {
        return;
      }

      // Remove existing layers and sources
      this.removeExistingLayers();

      // Transform the recording data to include routeStyle property
      const transformedGeoJson = this.transformRecordingForStyling(this.geojson);

      // Get start and end markers for the recording
      const { startMarkers, endMarkers } = this.getRecordingStartEndMarkers(this.geojson);

      // Add source using LayerRegistry
      window.layerRegistry.addSource('recording-line', {
        type: 'geojson',
        data: transformedGeoJson,
      });

      // Create color expression based on route style - recordings only use 'route' style
      const colorExpression = ROUTE_STYLES.route.color;

      // Create border color expression based on route style - recordings only use 'route' style
      const borderColorExpression = ROUTE_STYLES.route.borderColor;

      // Create dash array expression - recordings use solid lines
      const dashArrayExpression = [1, 0];

      // Add border layer first (wider line with border color)
      window.layerRegistry.addLayer(
        {
          id: 'recording-line-border',
          type: 'line',
          source: 'recording-line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': borderColorExpression,
            'line-width': 7, // Wider for border
            'line-opacity': 1.0,
            'line-dasharray': dashArrayExpression,
          },
        },
        MapLayerCategory.Annotations,
        { z: 8 } // Lower z-order so border appears behind main line
      );

      // Add main line layer (thinner line with main color)
      window.layerRegistry.addLayer(
        {
          id: 'recording-line',
          type: 'line',
          source: 'recording-line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': colorExpression,
            'line-width': 6,
            'line-dasharray': dashArrayExpression,
          },
        },
        MapLayerCategory.Annotations,
        { z: 9 } // Higher z-order so main line appears on top
      );

      // Map zoom levels to icon sizes so the markers scale smoothly but cap at 0.3 after zoom 12.5.
      // Note: avoiding intermediate stops near 12 keeps Mapbox from snapping the size when crossing integer zooms.
      const markerIconSizeExpression = [
        'interpolate',
        ['linear'],
        ['zoom'],
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        0,
        0.05,
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        9,
        0.1,
        // zoom 12.5 -> size 0.3 (maximum size reached here)
        12,
        0.3,
        // maintain the cap for any higher zoom levels
        22,
        0.3
      ];

      // Add end markers before start markers so start markers can sit above
      if (endMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'recording-markers-end',
            type: 'symbol',
            source: 'recording-markers-end',
            layout: {
              'icon-image': 'hike-control-point-end',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'recording-markers-end',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: endMarkers
                },
              },
            },
            z: 20,
          }
        );
      }

      if (startMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'recording-markers-start',
            type: 'symbol',
            source: 'recording-markers-start',
            layout: {
              'icon-image': 'hike-control-point-start',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'recording-markers-start',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: startMarkers
                },
              },
            },
            z: 25,
          }
        );
      }

      // set the bounds
      const bounds = new mapboxgl.LngLatBounds();
      const { geometry } = this.geojson.features[0];
      geometry.coordinates.forEach((coord) => {
        bounds.extend([coord[0], coord[1]]);
      });
      window.map.fitBounds(bounds, { padding: 150 });
      this.loading = false;
    },
    // Recording-specific options methods
    editRecording() {
      // Edit recording details - call the global function that loads the data
      // Use the stored recordingSlug to ensure we're using the exact same value that successfully fetched the data
      if (this.recordingSlug && typeof window.showEditRecordingModal === 'function') {
        console.log('Opening edit modal with slug:', this.recordingSlug);
        window.showEditRecordingModal(this.recordingSlug);
      } else {
        console.error('Unable to edit recording: slug not available or modal function not found', {
          slug: this.recordingSlug,
          hasFunction: typeof window.showEditRecordingModal === 'function'
        });
      }
    },

    editRecordingInPlanner() {
      // Edit recording in hike planner
      if (this.recording?.id) {
        window.location.href = `/hike-planner?trail_type=Recording&trail_id=${this.recording.id}`;
      }
    },
    openWaypoints() {
      const el = document.getElementById('waypoint-options-dialog');
      if (el) {
        Modal.getOrCreateInstance(el).show();
      }
      const mo = document.getElementById('map-options-dialog');
      if (mo) {
        Modal.getOrCreateInstance(mo).hide();
      }
    },
    showGoTo() {
      const el = document.getElementById('go-to-location-dialog');
      if (el) { Modal.getOrCreateInstance(el).show(); }
      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    showFlyover() {
      // Open the interactive 3D flyover of this recording. The launcher
      // (window.showMapModalForRecordedTrail) is mounted by FlyoverPreview.vue,
      // which is rendered on the recording public map page.
      if (this.recording?.id && typeof window.showMapModalForRecordedTrail === 'function') {
        // Pass the recording's share config so the preview's "Share video" button works from here.
        // (The map page has no ".generate-3d-flyover" dataset to fall back on.) The style is added
        // by the preview itself from its own current map style.
        window.showMapModalForRecordedTrail(
          this.recording.id,
          buildRecordingShareConfig(this.recording, MeasurementPrefUtility.preference()),
        );
      } else {
        window.alert('Unable to open 3D Flyover: recording data not available yet.');
      }
    },
    openPrintMap() {
      // Print recording map
      if (this.recording?.id) {
        window.open(`/print-maps?data_ids[]=${this.recording.id}&data_types[]=Recording`, '_blank');
      }
    },
    async showMeasurement() {
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }

      // For recordings, use the measurement tool with the recording route
      if (this.geojson) {
        // Transform the recording route to match the measurement tool's expected format
        const measureableGeoJson = this.transformRecordingForMeasurement(this.geojson);

        // Set the measureable GeoJSON data for the measurement tool
        await this.$store.dispatch('hikeMeasurement/setMeasureableGeoJson', measureableGeoJson);
        // Start measurement mode
        await this.$store.dispatch('hikeMeasurement/startMeasurementMode');
      } else {
        window.alert('Unable to start measurement: Recording route data not available');
      }

      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    reportIssue() {
      // Report issue for recording
      if (!this.recording?.id) {
        window.alert('Unable to report issue: Recording ID not found');
        return;
      }

      try {
        // Construct the report issue URL based on the current page URL
        const currentPath = window.location.pathname;
        const reportUrl = currentPath.replace(/\/map$/, '/report_issue');

        // Submit the report issue request
        fetch(reportUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        }).then(response => response.json())
          .then(data => {
            if (data.success) {
              window.alert('Thank you for reporting this issue. Our team has been notified.');
            } else {
              window.alert('Failed to report issue. Please try again.');
            }
          })
          .catch(error => {
            console.error('Error reporting issue:', error);
            window.alert('Failed to report issue. Please try again.');
          });
      } catch (error) {
        console.error('Error reporting issue:', error);
        window.alert('Failed to report issue. Please try again.');
      }
    },
    createNewHike() {
      // Create a new hike from the current recording
      let mapCenter = window.map.getCenter();
      let lat = mapCenter.lat;
      let lon = mapCenter.lng;
      let zoom = Math.round(window.map.getZoom());
      window.location.href = `/hike-planner?lat=${lat}&lon=${lon}&zoom=${zoom}`;
    },

    // Helper method to remove existing layers and sources
    removeExistingLayers() {
      const layersToRemove = ['line', 'recording-line', 'recording-line-border', 'recording-markers-start', 'recording-markers-end'];
      layersToRemove.forEach(layerId => {
        if (window.map.getLayer(layerId)) {
          window.map.removeLayer(layerId);
        }
        if (window.map.getSource(layerId)) {
          window.map.removeSource(layerId);
        }
      });
    },

    // Transform recording data to include routeStyle property for styling
    transformRecordingForStyling(geojson) {
      // Recordings should use the 'route' style by default
      const transformedFeatures = [];

      if (geojson.features) {
        geojson.features.forEach((feature) => {
          const transformedFeature = {
            ...feature,
            properties: {
              ...feature.properties,
              routeStyle: 'route' // Recordings use the main route style
            }
          };
          transformedFeatures.push(transformedFeature);
        });
      } else if (geojson.type === 'LineString') {
        // Handle case where geojson is a single LineString
        const transformedFeature = {
          type: 'Feature',
          geometry: geojson,
          properties: {
            routeStyle: 'route' // Recordings use the main route style
          }
        };
        transformedFeatures.push(transformedFeature);
      }

      return {
        type: 'FeatureCollection',
        features: transformedFeatures
      };
    },

    // Get start and end markers for recording
    getRecordingStartEndMarkers(geojson) {
      const result = { startMarkers: [], endMarkers: [] };

      if (!geojson) {
        return result;
      }

      // Extract coordinates from the recording route
      let coordinates = [];

      if (geojson.features && geojson.features.length > 0) {
        // Handle FeatureCollection
        const feature = geojson.features[0];
        if (feature.geometry.type === 'LineString') {
          coordinates = feature.geometry.coordinates;
        } else if (feature.geometry.type === 'MultiLineString') {
          // Use the first line string
          coordinates = feature.geometry.coordinates[0] || [];
        }
      } else if (geojson.type === 'LineString') {
        // Handle single LineString
        coordinates = geojson.coordinates;
      }

      if (coordinates.length < 2) {
        return result;
      }

      // Create start marker
      const startMarker = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coordinates[0]
        },
        properties: {
          markerType: 'start'
        }
      };

      // Create end marker
      const endMarker = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coordinates[coordinates.length - 1]
        },
        properties: {
          markerType: 'end'
        }
      };

      result.startMarkers.push(startMarker);
      result.endMarkers.push(endMarker);

      return result;
    },
    closeMobilePanels() {
      // For mobile compatibility - no mobile panels in recording map currently
    },

    // Transform recording route data to match measurement tool's expected format
    transformRecordingForMeasurement(geojson) {
      // The measurement tool expects MultiLineString features with routeStyle properties
      const transformedFeatures = [];

      if (geojson.features) {
        geojson.features.forEach((feature) => {
          if (feature.geometry.type === 'LineString') {
            // Convert LineString to MultiLineString format expected by measurement tool
            const multiLineStringFeature = {
              type: 'Feature',
              geometry: {
                type: 'MultiLineString',
                coordinates: [feature.geometry.coordinates]
              },
              properties: {
                routeStyle: 'route' // Mark as main route for measurement snapping
              }
            };
            transformedFeatures.push(multiLineStringFeature);
          }
        });
      } else if (geojson.type === 'LineString') {
        // Handle case where geojson is a single LineString
        const multiLineStringFeature = {
          type: 'Feature',
          geometry: {
            type: 'MultiLineString',
            coordinates: [geojson.coordinates]
          },
          properties: {
            routeStyle: 'route' // Mark as main route for measurement snapping
          }
        };
        transformedFeatures.push(multiLineStringFeature);
      }

      return {
        type: 'FeatureCollection',
        features: transformedFeatures
      };
    },
  },
  beforeDestroy() {
    // Clean up event listener when component is destroyed
    window.removeEventListener('resize', this.resizeMap);
    emitter.off('map-style-loaded', this.addToMap);

    // Clean up map resources
    if (window.map) {
      // Remove sources and layers
      this.removeExistingLayers();

      // Remove the map instance
      window.map.remove();
      window.map = null;
    }
  },
};
</script>

<style scoped>
.mapboxgl-map{
  font-family: Montserrat, Helvetica, Arial, sans-serif;
}

.map-container {
  width: 100%;
  /* Leaves room for the breadcrumbs/share bar above the map (desktop-only,
     hidden below 768px), matching hike_map's 90→84svh reduction. */
  height: 84svh;
  position: relative;
}

@media (max-width: 768px) {
  .map-container {
    height: 90svh;
  }
}

@media (max-width: 520px){
  .map-buttons-container{
    bottom: 190px !important;
  }
}
</style>
